import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, CheckCircle2, Sparkles, Target, ShieldCheck, TrendingUp,
  Hammer, Factory, Building2, Eye, Zap,
} from "lucide-react";

export default function About() {
  useEffect(() => {
    document.title = "About — LumberLoop USA";
    const meta = document.querySelector('meta[name="description"]') || (() => {
      const m = document.createElement("meta");
      m.setAttribute("name", "description"); document.head.appendChild(m); return m;
    })();
    meta.setAttribute("content", "LumberLoop USA is a Sales Intelligence Platform for building product manufacturers — built to eliminate the lead-tracking black hole between your website, your dealers, and your revenue.");
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="about-page" className="bg-white text-[#17324D]">
      {/* HERO — dark cockpit matching Solution section */}
      <section className="relative bg-[#0A1B30] py-24 lg:py-28 overflow-hidden border-b border-slate-800">
        <div className="absolute inset-0 opacity-[0.07]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)`,
          backgroundSize: "56px 56px",
        }} aria-hidden="true"/>
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[900px] h-[400px] rounded-full bg-[#C62828]/12 blur-3xl" aria-hidden="true"/>
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#C62828]/60 to-transparent" aria-hidden="true"/>

        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C62828] animate-pulse"/>
            <span className="font-tech-mono text-[10px] tracking-[0.28em] uppercase text-slate-300 font-semibold">About LumberLoop USA</span>
          </div>
          <h1 className="font-tech font-bold text-4xl md:text-6xl lg:text-7xl mt-6 leading-[0.98] text-white">
            A Sales Intelligence Platform<br/>
            <span className="text-[#C62828]">not another directory.</span>
          </h1>
          <p className="mt-6 text-lg md:text-xl text-slate-300 font-body leading-relaxed max-w-3xl mx-auto">
            Built to bridge the gap between your website and your revenue — with total visibility into every dealer, every lead, every outcome.
          </p>
        </div>
      </section>

      {/* MISSION — dark, easy to read */}
      <MissionSection/>

      {/* THE STORY */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="label-tag">The Story</div>
          <h2 className="font-display text-3xl md:text-5xl mt-4 leading-[1.05] text-[#17324D]">
            Manufacturers have been <span className="text-[#C62828]">flying blind.</span>
          </h2>

          <div className="mt-8 space-y-5 text-lg text-slate-700 leading-relaxed font-body">
            <p>
              For too long, building product manufacturers have been flying blind. Leads are generated, passed to dealers, and then lost in a cycle of manual follow-ups, fragmented communication, and missed opportunities.
            </p>
            <p className="font-heading text-xl md:text-2xl text-[#17324D] border-l-4 border-[#C62828] pl-5 py-2">
              We built LumberLoop USA to change that.
            </p>
            <p>
              We are <span className="font-semibold text-[#17324D]">not a directory.</span> We are a <span className="font-semibold text-[#C62828]">Sales Intelligence Platform</span> designed to bridge the gap between your website and your revenue.
            </p>
            <p>
              By providing a centralized, high-performance dashboard, we empower manufacturers to <span className="font-semibold text-[#17324D]">track dealer engagement</span>, <span className="font-semibold text-[#17324D]">verify lead follow-up</span>, and ensure that every high-intent lead results in a closed deal.
            </p>
          </div>
        </div>
      </section>

      {/* WHAT WE DO — three audience cards */}
      <section className="bg-slate-50 border-y border-slate-200 py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="max-w-3xl mx-auto text-center">
            <div className="label-tag justify-center">What We Do</div>
            <h2 className="font-display text-3xl md:text-5xl mt-4 leading-[1.05] text-[#17324D]">
              A strategic partner for the <span className="text-[#C62828]">modern manufacturer.</span>
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-5">
            <ValueCard
              icon={Factory}
              audience="For Manufacturers"
              primary
              items={[
                "Generate qualified contractor demand",
                "Increase dealer sales and conversion",
                "Introduce products into new markets",
                "Gain insight into market activity and buying trends",
                "Close the loop on the existing black hole in your channel",
              ]}
            />
            <ValueCard
              icon={Building2}
              audience="For Dealers"
              items={[
                "Receive contractor opportunities already looking to buy",
                "Increase quote volume",
                "Win new business",
                "Strengthen manufacturer and contractor relationships",
              ]}
            />
            <ValueCard
              icon={Hammer}
              audience="For Contractors"
              items={[
                "Find products faster",
                "Submit one request instead of multiple phone calls",
                "Receive competitive quotes from local suppliers",
                "Discover new products that can improve contractor and customer experience",
              ]}
            />
          </div>
        </div>
      </section>

      {/* BUILT BY INDUSTRY */}
      <section className="bg-white py-20 lg:py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-10">
          <div className="label-tag">Who Built This</div>
          <h2 className="font-display text-3xl md:text-5xl mt-4 leading-[1.05] text-[#17324D]">
            Built by <span className="text-[#C62828]">industry professionals.</span>
          </h2>
          <div className="mt-6 space-y-4 text-lg text-slate-700 leading-relaxed font-body">
            <p>
              LumberLoop USA wasn&apos;t created by software engineers trying to learn the building industry. It was built by professionals who have spent decades working with contractors, lumberyards, distributors, manufacturers, architects, and building product sales teams.
            </p>
            <p>
              We&apos;ve lived the daily challenges of selling, specifying, quoting, and sourcing building materials — and we built LumberLoop USA to eliminate the inefficiencies we experienced firsthand.
            </p>
            <p className="font-heading text-xl md:text-2xl text-[#17324D] border-l-4 border-[#C62828] pl-5 py-2">
              Relationships matter. Speed, communication, and accountability matter just as much.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#0A1B30] py-20 overflow-hidden border-t border-slate-800">
        <div className="absolute inset-0 opacity-[0.07]" style={{
          backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)`,
          backgroundSize: "56px 56px",
        }} aria-hidden="true"/>
        <div className="absolute -top-24 right-1/4 w-[500px] h-[400px] rounded-full bg-[#C62828]/10 blur-3xl" aria-hidden="true"/>

        <div className="relative max-w-4xl mx-auto px-6 lg:px-10 text-center">
          <h2 className="font-tech font-bold text-3xl md:text-5xl text-white leading-[1.02]">
            Ready to see <span className="text-[#C62828]">total accountability</span> in action?
          </h2>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="btn-cta" data-testid="about-cta-demo">
              Request Early-Access Demo <ArrowRight size={16}/>
            </Link>
            <Link to="/#solution" className="btn-ghost-light" data-testid="about-cta-platform">
              See the Platform <Zap size={16}/>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

function MissionSection() {
  const pillars = [
    { icon: Eye,         label: "Total Visibility",     pill: "Every Lead",    body: "Track every lead from click to close." },
    { icon: ShieldCheck, label: "Full Accountability",  pill: "Every Handoff", body: "Verify dealer follow-up on every opportunity." },
    { icon: TrendingUp,  label: "Revenue Intelligence", pill: "Every Outcome", body: "Turn engagement data into channel growth." },
  ];

  const [active, setActive] = React.useState(0);
  const [playing, setPlaying] = React.useState(true);
  const wrapRef = React.useRef(null);

  React.useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setActive((a) => (a + 1) % pillars.length), 2000);
    return () => clearInterval(id);
  }, [playing, pillars.length]);

  React.useEffect(() => {
    if (!wrapRef.current) return;
    const obs = new IntersectionObserver(
      ([entry]) => setPlaying(entry.isIntersecting),
      { threshold: 0.25 }
    );
    obs.observe(wrapRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="relative bg-[#0A1B30] py-20 lg:py-24 overflow-hidden border-b border-slate-800" data-testid="mission-section">
      <div className="absolute inset-0 opacity-[0.06]" style={{
        backgroundImage: `linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)`,
        backgroundSize: "56px 56px",
      }} aria-hidden="true"/>
      <div className="absolute -top-24 left-1/4 w-[500px] h-[400px] rounded-full bg-[#C62828]/8 blur-3xl" aria-hidden="true"/>

      <div className="relative max-w-5xl mx-auto px-6 lg:px-10">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10">
            <span className="w-1.5 h-1.5 rounded-full bg-[#C62828]" style={{ animation: "sol-blink 1.4s ease-in-out infinite" }}/>
            <span className="font-tech-mono text-[10px] tracking-[0.28em] uppercase text-slate-300 font-semibold">Our Mission</span>
          </div>
          <h2 className="font-tech font-bold text-3xl md:text-5xl mt-6 leading-[1.05] text-white">
            Eliminate the <span className="text-[#C62828]">lead-tracking black hole</span> in the building products industry.
          </h2>
          <p className="mt-6 text-lg md:text-xl text-slate-200 leading-relaxed max-w-3xl mx-auto font-body">
            We provide manufacturers with <span className="text-white font-semibold">total visibility</span> and <span className="text-white font-semibold">accountability</span> in their dealer-partner network.
          </p>
        </div>

        <div ref={wrapRef} className="mt-14 relative">
          {/* Beam behind cards */}
          <div className="hidden md:block absolute top-1/2 left-0 right-0 -translate-y-1/2 z-0 pointer-events-none" style={{ height: 2 }}>
            <div className="mx-auto h-full max-w-[calc(100%-6rem)] bg-white/8"/>
            <div
              className="absolute top-1/2 left-0 h-3 w-3 -translate-y-1/2 rounded-full bg-[#C62828]"
              style={{
                boxShadow: "0 0 0 6px rgba(198,40,40,0.18), 0 0 24px rgba(198,40,40,0.7)",
                animation: "sol-beam 6s linear infinite",
              }}
            />
          </div>

          <div className="grid md:grid-cols-3 gap-4 lg:gap-5 relative z-10">
            {pillars.map((p, i) => {
              const isActive = active === i;
              const isDone = i < active;
              return (
                <div
                  key={p.label}
                  onMouseEnter={() => { setPlaying(false); setActive(i); }}
                  onMouseLeave={() => setPlaying(true)}
                  className={`group relative rounded-2xl p-6 lg:p-7 transition-all duration-500 cursor-default overflow-hidden ${
                    isActive
                      ? "bg-[#122744] border border-[#C62828]/60 shadow-[0_30px_60px_-12px_rgba(198,40,40,0.32)] -translate-y-1"
                      : "bg-white/[0.03] border border-white/10 hover:border-white/20 hover:bg-white/[0.05]"
                  }`}
                  data-testid={`mission-pillar-${i+1}`}
                >
                  <div className="relative flex items-center gap-2">
                    <div className={`flex items-center gap-1.5 px-2 py-1 rounded-md border text-[9px] font-tech-mono font-semibold uppercase tracking-[0.16em] transition-all duration-500 ${
                      isActive ? "bg-[#C62828]/20 border-[#C62828]/50 text-[#FCA5A5]" : "bg-white/5 border-white/10 text-slate-400"
                    }`}>
                      <span className={`w-1 h-1 rounded-full ${isActive ? "bg-[#C62828]" : "bg-slate-500"}`} style={isActive ? { animation: "sol-blink 1.2s ease-in-out infinite" } : {}}/>
                      {isActive ? "Live" : (isDone ? "Done" : "Standby")}
                    </div>
                    <span className="font-tech-mono text-[10px] tracking-widest uppercase text-slate-500">{p.pill}</span>
                  </div>

                  <div className="relative mt-5">
                    <div
                      className={`w-14 h-14 rounded-xl flex items-center justify-center transition-all duration-500 ${
                        isActive
                          ? "bg-[#C62828] text-white scale-110 shadow-[0_0_40px_-4px_rgba(198,40,40,0.9)]"
                          : "bg-white/8 text-white/70"
                      }`}
                    >
                      <p.icon size={22} strokeWidth={2}/>
                    </div>
                    {isActive && (
                      <span className="absolute inset-0 w-14 h-14 rounded-xl bg-[#C62828] opacity-40 pointer-events-none" aria-hidden="true"
                            style={{ animation: "sol-ripple 2s ease-out infinite" }}/>
                    )}
                  </div>

                  <h3 className={`font-tech font-bold text-xl md:text-2xl mt-5 tracking-tight transition-colors duration-500 ${
                    isActive ? "text-white" : "text-white/90"
                  }`}>
                    {p.label}
                  </h3>
                  <p className={`mt-2 text-sm md:text-base font-body leading-relaxed transition-colors duration-500 ${
                    isActive ? "text-slate-200" : "text-slate-400"
                  }`}>
                    {p.body}
                  </p>
                </div>
              );
            })}
          </div>

          <div className="mt-8 mx-auto max-w-md">
            <div className="flex items-center justify-between mb-2">
              <span className="font-tech-mono text-[10px] tracking-[0.24em] uppercase text-slate-500">Mission</span>
              <span className="font-tech-mono text-[10px] tracking-[0.24em] uppercase text-[#FCA5A5]">
                {String(active + 1).padStart(2, "0")} / {String(pillars.length).padStart(2, "0")}
              </span>
            </div>
            <div className="h-[3px] rounded-full bg-white/8 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-[#C62828] to-[#F87171] transition-all duration-700 ease-out"
                style={{ width: `${((active + 1) / pillars.length) * 100}%`, boxShadow: "0 0 12px rgba(198,40,40,0.7)" }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


function ValueCard({ icon: Icon, audience, items, primary = false }) {
  return (
    <div
      className={`rounded-xl p-6 lg:p-7 transition-all ${
        primary
          ? "bg-[#0A1B30] border border-[#C62828]/40 text-white shadow-[0_20px_50px_-12px_rgba(198,40,40,0.25)]"
          : "bg-white border border-slate-200 hover:border-[#17324D]/30 hover:shadow-md"
      }`}
      data-testid={`value-${audience.replace(/\s+/g, "-").toLowerCase()}`}
    >
      <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${primary ? "bg-[#C62828] text-white shadow-[0_0_24px_-4px_rgba(198,40,40,0.7)]" : "bg-[#17324D] text-white"}`}>
        <Icon size={22}/>
      </div>
      <div className={`font-tech-mono text-[10px] tracking-[0.28em] uppercase mt-4 font-semibold ${primary ? "text-[#FCA5A5]" : "text-slate-500"}`}>
        {primary && "★ "}{audience}
      </div>
      <ul className="mt-3 space-y-2.5">
        {items.map((it) => (
          <li key={it} className={`flex items-start gap-2 text-sm font-body ${primary ? "text-slate-200" : "text-slate-700"}`}>
            <CheckCircle2 size={16} className={`flex-shrink-0 mt-0.5 ${primary ? "text-[#FCA5A5]" : "text-[#C62828]"}`}/>
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
